import {
  type AggregatedEventOverviewResultBody,
  type EventOverviewESResultBody,
  type EventOverviewResult,
  type TeamPlayers,
} from '~/search-domain/pipelines/event-overview/types';
import { type MatchupCentreESResult } from '~/search-domain/pipelines/match-centre/types';
import { type Loggable, type SearchDomainAggregations, type SearchDomainHitWithSource } from '~/search-domain/types';
import { type BettingInsightsElasticSearchContext } from '~/search-domain/types/contracts';
import { replaceResponseHits } from '~/search-domain/utils';

export type PlayerAggregationBucket = {
  doc_count: number;
  key: string;
  name_value: {
    hits: {
      hits: Array<{
        _source: {
          sportsData: {
            participant: {
              name: string;
              number?: number;
              participantId: string;
              position?: string;
              team?: {
                badgeUrl?: string;
                name: string;
                nickName?: string;
                participantId: string;
                shortName?: string;
                side: 'HOME' | 'AWAY';
              };
              type: 'PLAYER';
            };
          };
        };
      }>;
    };
  };
};

export type PlayersAggregation = {
  buckets: PlayerAggregationBucket[];
};

export type OriginalAggregations = {
  players: PlayersAggregation;
};

export type { EventOverviewESResultBody, AggregatedEventOverviewResultBody, TeamPlayers };

export async function createResponseFactory(
  this: Loggable,
  _: BettingInsightsElasticSearchContext,
  searchResponse: EventOverviewESResultBody,
): Promise<AggregatedEventOverviewResultBody> {
  const teamPlayers = collectTeamPlayers(searchResponse.aggregations as OriginalAggregations | undefined);
  const representativeHit = createRepresentativeHit(searchResponse.aggregations, teamPlayers);
  const baseAggregations = stripEventOverviewAggregations(searchResponse.aggregations);

  return replaceResponseHits(searchResponse, {
    aggregations: baseAggregations as SearchDomainAggregations,
    hits: representativeHit ? [representativeHit] : [],
    totalRelation: 'eq',
    totalValue: 1,
  });
}

type PlayerEntry = TeamPlayers['home'][number];
type EventInfoAggregation = {
  event_info?: { hits?: { hits?: Array<SearchDomainHitWithSource<MatchupCentreESResult>> } };
};
type EventOverviewHit = SearchDomainHitWithSource<EventOverviewResult>;

function toPlayerEntry(bucket: PlayerAggregationBucket): { player: PlayerEntry; side?: 'HOME' | 'AWAY' } | null {
  const participant = bucket?.name_value?.hits?.hits?.[0]?._source?.sportsData?.participant;
  if (!participant) return null;
  return {
    side: participant.team?.side,
    player: {
      participantId: participant.participantId,
      name: participant.name,
      number: participant.number,
      position: participant.position,
    },
  };
}

function collectTeamPlayers(aggregations: OriginalAggregations | undefined): TeamPlayers {
  return (aggregations?.players?.buckets ?? []).map(toPlayerEntry).reduce<TeamPlayers>(
    (acc, entry) => {
      if (!entry) return acc;
      if (entry.side === 'HOME') acc.home.push(entry.player);
      if (entry.side === 'AWAY') acc.away.push(entry.player);
      return acc;
    },
    { home: [], away: [] },
  );
}

function createRepresentativeHit(
  aggregations: SearchDomainAggregations | undefined,
  teamPlayers: TeamPlayers,
): EventOverviewHit | undefined {
  const firstEventInfoHit = (aggregations as EventInfoAggregation | undefined)?.event_info?.hits?.hits?.[0];
  const source = firstEventInfoHit?._source;
  if (!source) return undefined;

  const eventOverviewSource: EventOverviewResult = {
    ...source,
    betTypeId: undefined,
    players: teamPlayers,
  };

  return { ...firstEventInfoHit, _source: eventOverviewSource };
}

function stripEventOverviewAggregations(aggregations: Record<string, unknown> | undefined) {
  const {
    event_info: _eventInfo,
    players: _playersAgg,
    ...baseAggregations
  } = (aggregations || {}) as Record<string, unknown>;
  return baseAggregations;
}
